import {Form, Offcanvas} from "react-bootstrap";
import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Helmet} from "react-helmet";
import Loader from "./Loader";

const nf = new Intl.NumberFormat("he-IL");

// Assets live in an `assets` array inside the same user mix JSON as `mortgages`
// (each asset: {name, price, rental}). They are persisted with the mix and are
// intended to feed a future simulation; the current simulator ignores them.
function AssetsPage({user, albert}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token] = useState(localStorage.getItem("token"));

    useEffect(() => {
        const fetchData = async () => {
            const storedToken = localStorage.getItem("token");
            const startedAt = Date.now();
            try {
                const response = await fetch("/api/getuserjson", {
                    method: "POST",
                    headers: {"Content-Type": "application/json", Authorization: `Bearer ${storedToken}`},
                });
                if (!response.ok) throw new Error("bad request");
                const results = await response.json();
                setData(results);
            } catch (error) {
                setData({mortgages: []});
            } finally {
                // keep the spinner on screen for at least ~600ms so a fast load doesn't just flash
                const elapsed = Date.now() - startedAt;
                setTimeout(() => setLoading(false), Math.max(0, 600 - elapsed));
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const assets = (data && data.assets) || [];

    // add/edit offcanvas form
    const [showForm, setShowForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [assetName, setAssetName] = useState("");
    const [assetPrice, setAssetPrice] = useState("");
    const [assetRental, setAssetRental] = useState("");

    const openAdd = () => {
        setEditingIndex(null);
        setAssetName("");
        setAssetPrice("");
        setAssetRental("");
        setShowForm(true);
    };

    const openEdit = (index) => {
        const a = assets[index];
        setEditingIndex(index);
        setAssetName(a.name ?? "");
        setAssetPrice(a.price ?? "");
        setAssetRental(a.rental ?? "");
        setShowForm(true);
    };

    const removeAsset = (index) => {
        // spread ...d so the mortgages section is preserved in the saved JSON
        setData(d => ({...d, assets: (d.assets || []).filter((_, i) => i !== index)}));
    };

    // Exactly one asset can be chosen as the comparison asset: selecting one clears the rest.
    const selectAsset = (index) => {
        setData(d => ({...d, assets: (d.assets || []).map((a, i) => ({...a, selected: i === index}))}));
    };

    const handleSaveAsset = () => {
        const name = assetName.trim();
        if (name.length < 1) { albert("יש להזין שם לנכס"); return; }
        if (assetPrice === "" || isNaN(Number(assetPrice))) { albert("מחיר הנכס חייב להיות מספרי"); return; }
        if (assetRental === "" || isNaN(Number(assetRental))) { albert("שכר הדירה חייב להיות מספרי"); return; }
        const nameTaken = assets.some((a, i) => a.name === name && i !== editingIndex);
        if (nameTaken) { albert("שם הנכס כבר קיים, יש לבחור שם אחר"); return; }

        const newAsset = {name, price: Number(assetPrice), rental: Number(assetRental)};
        setData(d => {
            const list = [...((d && d.assets) || [])];
            if (editingIndex !== null) list[editingIndex] = newAsset;
            else list.push(newAsset);
            return {...d, assets: list};
        });
        setShowForm(false);
    };

    const handleSave = async () => {
        try {
            const response = await fetch("/api/putuserjson", {
                method: "POST",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                // assets don't affect the simulation: keep existing results, don't recompute
                body: JSON.stringify({userid: localStorage.getItem("userid"), userjson: JSON.stringify(data), run_simulation: false, clear_results: false}),
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const result = await response.json();
            setData(result);
            albert("הנכסים נשמרו");
        } catch (error) {
            albert("רק משתמש רשום ומחובר יכול לשמור נתונים");
        }
    };

    if (user.id === null) return (
        <>
            <Helmet>
                <title>מחשבון משכנתא | נכסים</title>
                <meta name="description" content="ניהול נכסים"/>
            </Helmet>
            <center>
                <br/>
                <h4>ניהול נכסים</h4>
                <p>לניהול הנכסים יש ליצור חשבון ולהתחבר.</p>
                <br/>
                <Link to="/login" className="align-items-center homepagebutton">כניסה</Link>
                <Link to="/register" className="align-items-center homepagebutton">הרשמה</Link>
            </center>
        </>
    );

    if (loading) return <Loader title="טוען את הנכסים שלך" text="רק רגע"/>;

    return (
        <div style={{padding: "15px"}}>
            <Helmet>
                <title>מחשבון משכנתא | הנכסים שלי</title>
                <meta name="description" content="עריכת הנכסים"/>
            </Helmet>

            <Offcanvas show={showForm} placement="end" onHide={() => setShowForm(false)} className="loan-offcanvas" backdropClassName="loan-offcanvas-backdrop">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <i className={`bi ${editingIndex !== null ? "bi-pencil-square" : "bi-plus-circle"}`}></i>
                        {editingIndex !== null ? "עריכת נכס" : "הוספת נכס"}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="loan-form-body">
                    <Form onSubmit={(e) => { e.preventDefault(); handleSaveAsset(); }}>
                        <div className="loan-form-section">
                            <Form.Label htmlFor="assetName"><i className="bi bi-tag"></i> שם הנכס</Form.Label>
                            <Form.Control id="assetName" type="input" value={assetName} autoFocus
                                          onChange={(e) => setAssetName(e.target.value)}/>
                            <div className="loan-form-hint">שם ייחודי לנכס, לדוגמה "דירה להשקעה בחיפה"</div>
                        </div>

                        <div className="loan-form-section">
                            <Form.Label htmlFor="assetPrice"><i className="bi bi-cash-stack"></i> מחיר הנכס (₪)</Form.Label>
                            <Form.Control id="assetPrice" type="number" value={assetPrice}
                                          onChange={(e) => setAssetPrice(e.target.value)}/>
                        </div>

                        <div className="loan-form-section">
                            <Form.Label htmlFor="assetRental"><i className="bi bi-house-door"></i> שכירות חודשית (₪)</Form.Label>
                            <Form.Control id="assetRental" type="number" value={assetRental}
                                          onChange={(e) => setAssetRental(e.target.value)}/>
                            <div className="loan-form-hint">דמי השכירות החודשיים הצפויים מהנכס</div>
                        </div>
                    </Form>

                    <div className="loan-form-actions">
                        <button className="addnew" onClick={handleSaveAsset}>
                            <i className={`bi ${editingIndex !== null ? "bi-check-lg" : "bi-plus-lg"}`}></i>
                            {editingIndex !== null ? "שמור שינויים" : "הוסף נכס"}
                        </button>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            <div id="margener">
                <div id="editor">
                    <div className="editor-toolbar">
                        <div className="editor-toolbar-title">
                            <h2 className="my-text-gradient">הנכסים שלי</h2>
                        </div>
                        <div className="editor-toolbar-actions">
                            <button onClick={openAdd} className="addnew"><i className="bi bi-plus-lg"></i> הוסף נכס</button>
                            <button className="savejson" onClick={handleSave}><i className="bi bi-save"></i> שמור</button>
                        </div>
                    </div>

                    {assets.length === 0 ? (
                        <div className="empty-state">
                            <i className="bi bi-building"></i>
                            <p>עדיין לא הוספתם נכסים. לחצו על "הוסף נכס" כדי להתחיל.</p>
                        </div>
                    ) : (
                        <div className="mortgages-grid">
                            {assets.map((asset, index) => (
                                <div className={`mortgage-card fade-in${asset.selected ? " asset-card--selected" : ""}`} key={asset.name}>
                                    <div className="mortgage-card-header">
                                        <div className="mortgage-title">
                                            <i className="bi bi-building"></i>
                                            <h4 className="mortgage-name">{asset.name}</h4>
                                            {asset.selected && <span className="loan-count-badge">נבחר להשוואה</span>}
                                        </div>
                                        <div className="mortgage-actions">
                                            <button className="icon-btn icon-btn-edit" onClick={() => openEdit(index)}>
                                                <i className="bi bi-pencil"></i> ערוך
                                            </button>
                                            <button className="icon-btn icon-btn-danger" onClick={() => removeAsset(index)}>
                                                <i className="bi bi-trash"></i> מחק
                                            </button>
                                        </div>
                                    </div>
                                    <div className="loan-stats-grid" style={{borderBottom: "none"}}>
                                        <div className="loan-stat">
                                            <span className="loan-stat-label">מחיר הנכס</span>
                                            <span className="loan-stat-value">{nf.format(asset.price)} ₪</span>
                                        </div>
                                        <div className="loan-stat">
                                            <span className="loan-stat-label">שכירות חודשית</span>
                                            <span className="loan-stat-value">{nf.format(asset.rental)} ₪</span>
                                        </div>
                                    </div>
                                    <label className="asset-select">
                                        <input type="radio" name="comparison-asset"
                                               checked={!!asset.selected} onChange={() => selectAsset(index)}/>
                                        <span>בחר נכס זה להשוואה</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AssetsPage;
